import {StyleSheet} from "aphrodite";
import {useCallback} from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {emitGuideMessageProducingAction} from "@khan/events-guide";
import {ANCHOR_ICON_SIZE} from "@khan/guide-core";

import type {GuideSidebarOpener} from "../opener/guide-sidebar-opener.tsx";

import {NotificationActionBubblesButton} from "./notification-action-bubbles/notification-action-bubbles-button.tsx";
import {NotificationActionBubbles} from "./notification-action-bubbles/notification-action-bubbles.tsx";
import {NotificationMessageWithCTA} from "./notification-message/notification-message-with-cta.tsx";

import "./notifications-animation.less";

import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";
import {useNotificationState} from "@khan/guide-notifications";
import type {ActionBubble} from "@khan/guide-types";

import {NotificationActionBubblesStateProvider} from "./notification-action-bubbles/state/provider.tsx";

interface Props {
    /**
     * A React element of type `GuideSidebarOpener` or `null` which will be
     * rendered as the toggle element for the guide sidebar.
     */
    children: React.ReactElement<typeof GuideSidebarOpener> | null;
    /**
     * An optional style override. Can be used by the parent to customize the
     * styles or positioning.
     */
    style?: StyleType;
}

/**
 * A component that connects the Khanmigo notification state to the UI.
 */
export const GuideSidebarNotifications = (props: Props): React.ReactElement => {
    const {children: toggleElement, style} = props;
    const prefersReducedMotion = usePrefersReducedMotion();
    const notificationState = useNotificationState();

    const isMessageStyle =
        notificationState.isShowing && notificationState.style === "message";

    const isActionBubblesStyle =
        notificationState.isShowing &&
        notificationState.style === "action-bubbles" &&
        !!notificationState.notification.actionBubbles?.length;

    const handleActionSelected = useCallback(
        (action: ActionBubble) => {
            // Currently, notifications only support sending messages to Khanmigo.
            // If we want to support other action types, we will need to update
            // this logic to leverage the use-make-action-bubble-handlers hook.
            emitGuideMessageProducingAction({
                text: action.command,
                requestUIType: isActionBubblesStyle
                    ? "NOTIFICATION_ACTION_BUBBLE"
                    : "NOTIFICATION_MESSAGE_CTA",
            });
        },
        [isActionBubblesStyle],
    );

    return (
        <View style={[styles.wrapper, style]}>
            {/* No notifications showing */}
            {!notificationState.isShowing && (
                <View style={styles.sidebarToggleWrapper}>{toggleElement}</View>
            )}

            {/* Showing a "message" style notification */}
            {isMessageStyle && (
                <>
                    <NotificationMessageWithCTA
                        {...notificationState.notification}
                        eventName={notificationState.eventName}
                        notificationId={notificationState.notificationId}
                        onActionSelected={handleActionSelected}
                    />
                    <View style={styles.sidebarToggleWrapper}>
                        {toggleElement}
                    </View>
                </>
            )}

            {/* Showing a "action-bubbles" style notification */}
            {isActionBubblesStyle && (
                <NotificationActionBubblesStateProvider
                    // We use the notificationId as the key to ensure that the
                    // component is re-mounted and state is reset when the
                    // notificationId changes.
                    key={notificationState.notificationId}
                    eventName={notificationState.eventName}
                    notification={notificationState.notification}
                    prefersReducedMotion={prefersReducedMotion}
                >
                    <NotificationActionBubbles
                        prefersReducedMotion={prefersReducedMotion}
                        style={styles.notifications}
                        onActionSelected={handleActionSelected}
                    />

                    <View style={styles.sidebarToggleWrapper}>
                        {toggleElement}

                        {/* If notification action bubbles are displayed, we
                            also render an accompanying button to collapse/
                            dismiss them */}
                        <NotificationActionBubblesButton
                            label={notificationState.notification.text}
                            style={!prefersReducedMotion && styles.fadeIn}
                        />
                    </View>
                </NotificationActionBubblesStateProvider>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    fadeIn: {
        animationName: "fadeIn",
        animationDuration: "400ms",
        animationTimingFunction: "ease-out",
    },
    notifications: {
        position: "absolute",
        top: 0,
        // Shift the notifications upwards 100% (height of the notifications) +
        // some margin in-between the notifications and toggle.
        transform: `translateY(calc(-100% - ${spacing.medium_16}px))`,
    },
    wrapper: {
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "flex-start",
        // We need to apply an explicit height to the wrapper so that the
        // top positioning of the notifications can be properly computed.
        height: ANCHOR_ICON_SIZE,
    },
    sidebarToggleWrapper: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "end",
        height: ANCHOR_ICON_SIZE,
        gap: spacing.xSmall_8,
    },
});
